import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  Portal,
} from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';
import { getCookie, removeCookie, setCookie } from '../../utils/cookies';
import { useConfig } from '../../Hooks/useConfig';
import { useAuthStatus } from '../../store/auth/useAuthStatus';
import { ArrowRight2 } from 'iconsax-react';

export const CustomizDropdown = styled(Box)(({ theme, openUpward }) => ({
  position: 'fixed',
  overflow: 'visible',
  width: 'auto',
  zIndex: 1300,
  backgroundColor: theme.palette.secondary.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.secondary.neutral_30 : '#E5E5E5'}`,
  borderRadius: theme.spacing(3),
  minWidth: '200px',
  maxWidth: '250px',
  boxShadow: '0px 0px 6px 0px rgba(0, 0, 0, 0.12)',
  '.MuiListItemButton-root': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.surface_22
          : theme.palette.primary.primary_99,
    },
  },
  [theme.breakpoints.down('md')]: {
    borderRadius: theme.spacing(3, 3, 0, 0),
    position: 'fixed',
    top: 'auto',
    bottom: '0',
    left: '0',
    right: '0',
    width: '100%',
    maxWidth: 'none',
    marginBottom: ({ isLogout }) => (isLogout ? theme.spacing(0.15) : 0),
  },
}));

const IconListImg = styled('img')(({ theme }) => ({
  width: theme.spacing(6),
  height: theme.spacing(6),
  objectFit: 'contain',
  [theme.breakpoints.down('md')]: {
    width: theme.spacing(5),
    height: theme.spacing(5),
  },
}));

const BgBlurContent = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
  backgroundColor: 'rgba(27, 27, 31, 0.2)',
  backdropFilter: 'blur(2.5px) ',
  zIndex: 998,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const ListText = styled(ListItemText)(({ theme }) => ({
  '.MuiListItemText-primary': {
    fontSize: '14px !important',
    fontWeight: 600,
    lineHeight: 'normal',
    fontFamily: "'Poppins', sans-serif",
    color:
      theme.palette.mode === 'dark'
        ? theme.palette.secondary.neutral_99
        : '#1A1B1F',
    paddingBottom: theme.spacing(0),
    [theme.breakpoints.down('md')]: {
      fontSize: '12px !important',
    },
  },
}));

const SubmenuDropdown = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: '100%',
  top: 0,
  backgroundColor: theme.palette.secondary.paper,
  border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.secondary.neutral_30 : theme.palette.secondary.neutral_90}`,
  borderRadius: theme.spacing(2),
  minWidth: '150px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  zIndex: 1000,
  '.MuiListItemButton-root': {
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.surface_22
          : theme.palette.primary.primary_99,
    },
  },
  [theme.breakpoints.down('md')]: {
    position: 'static',
    width: '100%',
    left: 'auto',
    top: 'auto',
    border: 'none',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    paddingLeft: theme.spacing(2),
  },
}));

const Dropdown = ({
  selected = () => {
    return;
  },
  list,
  children,
  sx,
  menuSx,
  rightItem = false,
  hasHeader = false,
  hasFooter = false,
  openUpward = false,
  onOpenChange,
  isLogout = false,
}) => {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up('sm'));
  const mdDown = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [openSubmenuIndex, setOpenSubmenuIndex] = useState(null);
  const [anchorPosition, setAnchorPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const dropdownContentRef = useRef(null);
  const { baseDomain } = useConfig();
  const { setToken } = useAuthStatus();

  const handleSelect = (option) => {
    setIsOpen(false);
  };

  const handleToggle = () => {
    if (!isOpen && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setAnchorPosition({
        top: openUpward ? rect.top : rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (onOpenChange) {
      onOpenChange(newOpenState);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        dropdownContentRef.current &&
        !dropdownContentRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        if (onOpenChange) {
          onOpenChange(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef, onOpenChange]);

  const Logout = async () => {
    removeCookie('token', baseDomain);
    removeCookie('showFeedbackBanner', baseDomain);
    removeCookie('credit_warning_login_shown', baseDomain);
    removeCookie('credit_warning_playground_shown', baseDomain);

    let theme = getCookie('darkMode'); //localStorage.getItem("darkMode");
    localStorage.clear();
    setCookie('darkMode', theme, 34560000, baseDomain);
    setToken('');
    await history.push('/login');
  };

  return (
    <Box
      sx={{ position: smUp ? 'relative' : 'static', ...sx }}
      ref={dropdownRef}
    >
      {isOpen && <BgBlurContent onClick={handleToggle}></BgBlurContent>}
      <Box
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
      >
        {children}
      </Box>
      {isOpen && (
        <Portal>
          <CustomizDropdown
            ref={dropdownContentRef}
            openUpward={openUpward}
            isLogout={isLogout}
            onClick={(e) => e.stopPropagation()}
            sx={{
              top: openUpward ? 'auto' : `${anchorPosition.top}px`,
              // bottom: openUpward ? `${window.innerHeight - anchorPosition.top}px` : 'auto',
              bottom: 85,
              left: `${anchorPosition.left}px`,
              marginBottom: openUpward ? 2 : 0,
              ...menuSx,
            }}
          >
            <List
              sx={{ py: { xs: 1, md: 2 }, px: 0 }}
              subheader={hasHeader ? hasHeader : false}
            >
              {list.map((listItem, index) =>
                listItem.submenu ? (
                  // Item with submenu
                  <ListItem
                    key={index}
                    disablePadding
                    sx={{ position: 'relative', display: 'block' }}
                    onMouseEnter={() =>
                      !mdDown ? setOpenSubmenuIndex(index) : null
                    }
                    onMouseLeave={() =>
                      !mdDown ? setOpenSubmenuIndex(null) : null
                    }
                  >
                    <ListItemButton
                      sx={{
                        py: { xs: 2, md: 3 },
                        px: { xs: 3, md: 4 },
                      }}
                      onClick={(e) => {
                        if (mdDown) {
                          e.stopPropagation();
                          setOpenSubmenuIndex(
                            openSubmenuIndex === index ? null : index
                          );
                        }
                      }}
                    >
                      <Box
                        sx={{ mr: { xs: 1.5, md: 2 }, marginBottom: '-7px' }}
                      >
                        {listItem.type && listItem.type === 'image' ? (
                          <IconListImg
                            src={`/static/img/icon/${listItem.icon}`}
                          />
                        ) : (
                          <listItem.icon
                            size={listItem.iconSize || (mdDown ? 18 : 20)}
                          />
                        )}
                      </Box>
                      <ListText primary={t(listItem.text)} />
                      <Box
                        sx={{
                          ml: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <ArrowRight2
                          size={mdDown ? 14 : 16}
                          style={{
                            transform:
                              openSubmenuIndex === index && mdDown
                                ? 'rotate(90deg)'
                                : 'rotate(0deg)',
                            transition: 'transform 0.2s',
                          }}
                        />
                      </Box>
                    </ListItemButton>
                    {openSubmenuIndex === index && (
                      <SubmenuDropdown>
                        <List sx={{ p: 0 }}>
                          {listItem.submenu.map((subItem, subIndex) => (
                            <ListItem key={subIndex} disablePadding>
                              <ListItemButton
                                sx={{
                                  py: { xs: 1.5, md: 2 },
                                  px: { xs: 3, md: 4 },
                                }}
                                onClick={() => {
                                  if (subItem.onClick) {
                                    subItem.onClick();
                                  }
                                  setIsOpen(false);
                                  setOpenSubmenuIndex(null);
                                }}
                              >
                                {subItem.icon && (
                                  <Box
                                    sx={{
                                      mr: { xs: 1.5, md: 2 },
                                      marginBottom: '-7px',
                                    }}
                                  >
                                    {subItem.type &&
                                    subItem.type === 'image' ? (
                                      <IconListImg
                                        src={`/static/img/icon/${subItem.icon}`}
                                      />
                                    ) : (
                                      <subItem.icon size={mdDown ? 18 : 20} />
                                    )}
                                  </Box>
                                )}
                                <ListText
                                  primary={t(subItem.text)}
                                  sx={{
                                    '.MuiListItemText-primary': {
                                      fontSize: `${theme.spacing(3.5)} !important`,
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </SubmenuDropdown>
                    )}
                  </ListItem>
                ) : listItem.link && !listItem.link.includes('https://') ? (
                  <Link key={index} to={listItem.link}>
                    <ListItem
                      sx={{ color: 'primary.contrastText' }}
                      key={listItem.icon}
                      disablePadding
                    >
                      <ListItemButton
                        sx={{
                          py: { xs: 2, md: 3 },
                          px: { xs: 3, md: 4 },
                        }}
                        onClick={listItem.logout ? Logout : handleSelect}
                      >
                        <Box
                          sx={{ mr: { xs: 1.5, md: 2 }, marginBottom: '-7px' }}
                        >
                          {listItem.type && listItem.type === 'image' ? (
                            <IconListImg
                              src={`/static/img/icon/${listItem.icon}`}
                            />
                          ) : (
                            <listItem.icon
                              size={listItem.iconSize || (mdDown ? 18 : 20)}
                            />
                          )}
                        </Box>
                        <ListText primary={t(listItem.text)} />
                      </ListItemButton>
                    </ListItem>
                  </Link>
                ) : listItem.link && listItem.link.includes('https://') ? (
                  <Typography component={'a'} key={index} href={listItem.link}>
                    <ListItem
                      sx={{ color: 'primary.contrastText' }}
                      key={listItem.icon}
                      disablePadding
                    >
                      <ListItemButton
                        sx={{
                          py: { xs: 2, md: 3 },
                          px: { xs: 3, md: 4 },
                        }}
                        onClick={listItem.logout ? Logout : handleSelect}
                      >
                        <Box
                          sx={{ mr: { xs: 1.5, md: 2 }, marginBottom: '-7px' }}
                        >
                          {listItem.type && listItem.type === 'image' ? (
                            <IconListImg
                              src={`/static/img/icon/${listItem.icon}`}
                            />
                          ) : (
                            <listItem.icon
                              size={listItem.iconSize || (mdDown ? 18 : 20)}
                            />
                          )}
                        </Box>
                        <ListText primary={t(listItem.text)} />
                      </ListItemButton>
                    </ListItem>
                  </Typography>
                ) : (
                  <ListItem
                    onClick={() => selected(listItem.value)}
                    key={index}
                    disablePadding
                  >
                    <ListItemButton
                      sx={{
                        py: { xs: 2, md: 3 },
                        px: { xs: 3, md: 4 },
                      }}
                      onClick={listItem.logout ? Logout : handleSelect}
                    >
                      <Box
                        sx={{ mr: { xs: 1.5, md: 2 }, marginBottom: '-7px' }}
                      >
                        {listItem.type && listItem.type === 'image' ? (
                          <IconListImg
                            src={`/static/img/icon/${listItem.icon}`}
                          />
                        ) : (
                          <listItem.icon
                            size={listItem.iconSize || (mdDown ? 18 : 20)}
                          />
                        )}
                      </Box>
                      {listItem.text ? (
                        <ListText primary={t(listItem.text)} />
                      ) : (
                        <Skeleton sx={{ width: '100%' }} animation="wave" />
                      )}
                    </ListItemButton>
                  </ListItem>
                )
              )}
            </List>
            {hasFooter ? hasFooter : null}
          </CustomizDropdown>
        </Portal>
      )}
    </Box>
  );
};

export default Dropdown;
